import * as React from 'react';

import { enumLotStatus, LotStatus } from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  TextField,
  Select,
  TextArea,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import { LucideCheck } from 'lucide-react';

import {
  createInventoryLotAtom,
  updateInventoryLotAtom,
} from '../../_graphql/mutationInventoryLot';
import { inventoryZonesByFacilityQueryAtom } from '../../_graphql/queryInventoryZones';
import { $remoteFacilities } from '../../_state/facilities.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InventoryLotRecord } from '../../types/inventoryLot';

type ZoneRecord = {
  id: string;
  zoneCode: string;
  name: string;
  zoneType: string;
  parentZoneId: string | null;
};

const emptyZonesAtom = atom({
  state: 'hasData' as const,
  data: { data: [] as ZoneRecord[] },
});

type LotDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingLot?: InventoryLotRecord | null;
};

export const LotDialog = ({
  isOpen,
  onClose,
  onSuccess,
  editingLot,
}: LotDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [lotNumber, setLotNumber] = React.useState('');
  const [facilityId, setFacilityId] = React.useState('');
  const [zoneId, setZoneId] = React.useState<string | undefined>(undefined);
  const [description, setDescription] = React.useState('');
  const [status, setStatus] = React.useState<LotStatus>(enumLotStatus.Active);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const selectedFacilityId = facilityId || editingLot?.facilityId;
  const shouldFetchZones = isOpen && !!selectedFacilityId;

  const $zonesQuery = React.useMemo(
    () =>
      shouldFetchZones
        ? loadable(
            inventoryZonesByFacilityQueryAtom(() => ({
              variables: { facilityId: selectedFacilityId },
            })),
          )
        : emptyZonesAtom,
    [shouldFetchZones, selectedFacilityId],
  );

  const zonesResult = useAtomValue($zonesQuery);
  const zonesData =
    zonesResult.state === 'hasData' ? zonesResult.data?.data : null;

  const createLot = useSetAtom(createInventoryLotAtom({}));
  const updateLot = useSetAtom(updateInventoryLotAtom({}));

  const isEditMode = !!editingLot;

  React.useEffect(() => {
    if (editingLot) {
      setLotNumber(editingLot.lotNumber);
      setFacilityId(editingLot.facilityId);
      setZoneId(editingLot.zoneId);
      setDescription(editingLot.description || '');
      setStatus(editingLot.status);
    } else {
      setLotNumber('');
      setFacilityId('');
      setZoneId(undefined);
      setDescription('');
      setStatus(enumLotStatus.Active);
    }
  }, [editingLot]);

  const handleClose = React.useCallback(() => {
    setLotNumber('');
    setFacilityId('');
    setZoneId(undefined);
    setDescription('');
    setStatus(enumLotStatus.Active);
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (!lotNumber.trim()) {
      toast.error(t.lotsListView.dialog.validation.lotNumberRequired);
      return;
    }

    if (!isEditMode && !facilityId) {
      toast.error(t.lotsListView.dialog.validation.facilityRequired);
      return;
    }

    try {
      setIsSubmitting(true);

      if (isEditMode && editingLot) {
        const zoneChanged = zoneId !== editingLot.zoneId;
        await updateLot({
          lotId: editingLot.id,
          lotNumber: lotNumber.trim(),
          zoneId: zoneChanged ? zoneId || null : undefined,
          description: description.trim() || undefined,
          status: status !== editingLot.status ? status : undefined,
        });
        toast.success(t.lotsListView.dialog.updateSuccess);
      } else {
        await createLot({
          lotNumber: lotNumber.trim(),
          facilityId,
          zoneId: zoneId || undefined,
          description: description.trim() || undefined,
        });
        toast.success(t.lotsListView.dialog.createSuccess);
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t.lotsListView.dialog.unknownError;
      toast.error(
        isEditMode
          ? t.lotsListView.dialog.updateError(errorMessage)
          : t.lotsListView.dialog.createError(errorMessage),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    lotNumber,
    facilityId,
    zoneId,
    description,
    status,
    isEditMode,
    editingLot,
    createLot,
    updateLot,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const facilityOptions = React.useMemo(
    () => facilities.map((f) => ({ key: f.id, label: f.name })),
    [facilities],
  );

  const zoneOptions = React.useMemo(() => {
    const zones = zonesData ?? [];
    return [
      { key: '', label: t.lotsListView.dialog.noLocation },
      ...zones.map((zone: ZoneRecord) => ({
        key: zone.id,
        label: `${zone.zoneCode} - ${zone.name}`,
      })),
    ];
  }, [zonesData, t]);

  const statusOptions = React.useMemo(
    () => [
      { key: enumLotStatus.Active, label: t.lotsListView.statusTypes.Active },
      { key: enumLotStatus.Closed, label: t.lotsListView.statusTypes.Closed },
    ],
    [t],
  );

  const canSubmit =
    Boolean(lotNumber.trim()) &&
    (isEditMode || Boolean(facilityId)) &&
    !isSubmitting;

  const dialogTitle = isEditMode
    ? t.lotsListView.dialog.editTitle
    : t.lotsListView.dialog.createTitle;

  const submitButtonText = isEditMode
    ? isSubmitting
      ? t.lotsListView.dialog.updating
      : t.lotsListView.dialog.update
    : isSubmitting
      ? t.lotsListView.dialog.creating
      : t.lotsListView.dialog.create;

  return (
    <ConsoleDialog
      className="@LotDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={dialogTitle}
      size="wrap"
      content={
        <div className="space-y-4">
          <TextField
            label={t.lotsListView.dialog.lotNumberLabel}
            value={lotNumber}
            onChange={setLotNumber}
            placeholder={t.lotsListView.dialog.lotNumberPlaceholder}
            isRequired
          />

          {!isEditMode && (
            <Select
              label={t.lotsListView.dialog.facilityLabel}
              value={facilityId}
              onChange={(key) => setFacilityId(String(key))}
              options={facilityOptions}
              placeholder={t.lotsListView.dialog.facilityPlaceholder}
              isRequired
            />
          )}

          {isEditMode && (
            <TextField
              label={t.lotsListView.dialog.facilityLabel}
              value={editingLot?.facilityName || ''}
              isDisabled
            />
          )}

          {selectedFacilityId && (
            <Select
              label={t.lotsListView.dialog.locationLabel}
              value={zoneId || ''}
              onChange={(key) => setZoneId(key ? String(key) : undefined)}
              options={zoneOptions}
              placeholder={t.lotsListView.dialog.locationPlaceholder}
            />
          )}

          <TextArea
            label={t.lotsListView.dialog.descriptionLabel}
            value={description}
            onChange={setDescription}
            placeholder={t.lotsListView.dialog.descriptionPlaceholder}
          />

          {isEditMode && (
            <Select
              label={t.lotsListView.dialog.statusLabel}
              value={status}
              onChange={(key) => setStatus(key as LotStatus)}
              options={statusOptions}
            />
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.lotsListView.dialog.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {submitButtonText}
          </Button>
        </div>
      }
    />
  );
};
